import React, { useMemo } from "react";
import { Box, Typography, Divider, Grid } from "@mui/material";

import {
  GradingDetailsGroupDTO,
  ParticipantGradingDTO,
} from "../types/ExerciseGrading";

import { TeamDTO } from "../types/teamMembers";
import { EventPhase } from "../enums/EventsPhaseEnum";
import { Exercise } from "../types/Exercise";

interface Props {
  exercises: Exercise[];
  results: ParticipantGradingDTO[];
}

const FONT_STYLE = { fontFamily: "Verdana", fontSize: "13px" };

const InterviewSummary: React.FC<Props> = ({ exercises, results }) => {
  const filteredResults = useMemo(() => {
    return results.filter((r) => {
      if (!r.team) return true;
      return r.team.eventPhase === EventPhase.Interview;
    });
  }, [results]);

  const { groupToExercise } = useMemo(() => {
    const groupToExercise = new Map<number, Exercise>();

    exercises.forEach((ex) => {
      ex.grading.forEach((g) => {
        groupToExercise.set(g.id, ex);
      });
    });

    return { groupToExercise };
  }, [exercises]);

  const resultsByExercise = useMemo(() => {
    const map: Record<string, ParticipantGradingDTO[]> = {};

    filteredResults.forEach((r) => {
      const exercise = groupToExercise.get(r.gradingDetailsGroupID);
      if (!exercise) return;

      map[exercise.id] ??= [];
      map[exercise.id].push(r);
    });

    return map;
  }, [filteredResults, groupToExercise]);

  const exerciseTotals = useMemo(() => {
    const perExercise: Record<string, number> = {};
    let grandTotal = 0;

    exercises.forEach((ex) => {
      let exerciseSum = 0;

      ex.grading.forEach((group) => {
        const list = filteredResults.filter(
          (r) => r.gradingDetailsGroupID === group.id,
        );

        const groupSum = list.reduce((acc, r) => acc + (r.points ?? 0), 0);

        const weightedGroupSum = groupSum * (group.averageFactor ?? 1);

        exerciseSum += weightedGroupSum;
      });

      perExercise[ex.id] = exerciseSum;
      grandTotal += exerciseSum;
    });

    return { perExercise, grandTotal };
  }, [exercises, filteredResults]);

  return (
    <Box sx={{ display: "flex", mt: 2, height: "100%" }}>
      <Box sx={{ flex: 1, overflowY: "auto", pr: 2 }}>
        <Typography
          sx={{ fontWeight: 800, fontSize: 16, fontFamily: "Verdana", mb: 2 }}
        >
          Grading summary
        </Typography>

        {exercises.map((exercise) => {
          const exerciseResults = resultsByExercise[exercise.id] ?? [];
          const byGroup: Record<number, ParticipantGradingDTO[]> = {};

          exerciseResults.forEach((r) => {
            byGroup[r.gradingDetailsGroupID] ??= [];
            byGroup[r.gradingDetailsGroupID].push(r);
          });

          return (
            <Box key={exercise.id} mb={4}>
              <Typography sx={{ fontWeight: 700, mb: 2, ...FONT_STYLE }}>
                {exercise.title}
              </Typography>

              {exercise.grading
                .slice()
                .sort((a, b) => a.index - b.index)
                .map((group: GradingDetailsGroupDTO) => {
                  const groupResults = byGroup[group.id] ?? [];

                  const teamMap = new Map<number, TeamDTO>();
                  let hasUnassigned = false;

                  groupResults.forEach((r) => {
                    if (r.team) teamMap.set(r.team.id, r.team);
                    else hasUnassigned = true;
                  });

                  const groupTeams: (TeamDTO | null)[] = [
                    ...Array.from(teamMap.values()),
                    ...(hasUnassigned ? [null] : []),
                  ];

                  return (
                    <Box key={group.id} mb={3}>
                      <Typography
                        sx={{ fontWeight: 600, mb: 1, ...FONT_STYLE }}
                      >
                        {group.name}
                        {group.averageFactor !== 1
                          ? ` (sum * ${group.averageFactor})`
                          : ""}
                      </Typography>

                      {group.gradingDetails
                        .slice()
                        .sort((a, b) => a.index - b.index)
                        .map((d) => {
                          const all = groupResults.filter(
                            (r) => r.gradingDetailsID === d.id,
                          );

                          return (
                            <Grid
                              container
                              alignItems="center"
                              sx={{ py: 0.5, px: 0.5 }}
                              key={d.id}
                            >
                              <Grid item sx={{ flex: 1 }}>
                                <Typography sx={FONT_STYLE}>
                                  {d.text}
                                </Typography>
                              </Grid>

                              {groupTeams.map((t, idx) => {
                                const r = t
                                  ? all.find((x) => x.team?.id === t.id)
                                  : all.find((x) => !x.team);

                                return (
                                  <Grid
                                    key={`${d.id}-${t ? t.id : "u"}-${idx}`}
                                    item
                                    sx={{
                                      width: 90,
                                      textAlign: "center",
                                      ml: 1,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ...FONT_STYLE,
                                        color: r?.correction
                                          ? "red"
                                          : "inherit",
                                      }}
                                    >
                                      {r?.points?.toFixed(2) ?? "-"}
                                    </Typography>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          );
                        })}
                    </Box>
                  );
                })}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          width: 240,
          pl: 2,
          borderLeft: "1px solid #ddd",
          position: "sticky",
          top: 0,
          height: "fit-content",
        }}
      >
        {exercises.map((ex) => {
          const exerciseResults = resultsByExercise[ex.id] ?? [];
          const hasCorrection = exerciseResults.some((r) => r.correction);

          return (
            <Box key={ex.id} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, ...FONT_STYLE }}>
                {ex.title}
              </Typography>

              <Typography
                sx={{
                  ...FONT_STYLE,
                  mt: 0.5,
                  color: hasCorrection ? "red" : "inherit",
                  fontWeight: hasCorrection ? 700 : 400,
                }}
              >
                {exerciseTotals.perExercise[ex.id]?.toFixed(2) ?? "0.00"}
              </Typography>
            </Box>
          );
        })}

        <Divider sx={{ my: 2 }} />

        <Typography
          sx={{ fontFamily: "Verdana", fontSize: 16, fontWeight: 800 }}
        >
          Total
        </Typography>

        <Typography
          sx={{ fontFamily: "Verdana", fontSize: 14, fontWeight: 700, mt: 1 }}
        >
          {exerciseTotals.grandTotal.toFixed(2)}
        </Typography>
      </Box>
    </Box>
  );
};

export default InterviewSummary;
